import { FC, ReactNode, createContext, useEffect } from 'react';
import { AsyncState } from 'react-use/lib/useAsyncFn';
import { useFetchMigrationSteps } from '../hooks/migration/useFetchMigrationSteps';
import { useFetchResourceTypes } from '../hooks/migration/useFetchResourceTypes';
import { useFetchTemplateUpdates } from '../hooks/migration/useFetchTemplateUpdates';
import { MigrationStep, ResourceType, TemplateEntityUpdate } from '../types';

export type MigrationContextProps = {
  fetchResourceTypes: () => Promise<ResourceType[]>;
  fetchResourceTypesState: AsyncState<ResourceType[]>;

  fetchTemplateUpdates: () => Promise<TemplateEntityUpdate[]>;
  fetchTemplateUpdatesState: AsyncState<TemplateEntityUpdate[]>;

  fetchMigrationSteps: () => Promise<MigrationStep[]>;
  fetchMigrationStepsState: AsyncState<MigrationStep[]>;
};

export const MigrationContext = createContext<MigrationContextProps>(
  {} as MigrationContextProps,
);

interface Props {
  children?: ReactNode;
}

export const MigrationContextProvider: FC<Props> = ({ children }) => {
  const { fetchResourceTypesState, fetchResourceTypes } =
    useFetchResourceTypes();

  const { fetchTemplateUpdatesState, fetchTemplateUpdates } =
    useFetchTemplateUpdates();

  const { fetchMigrationStepsState, fetchMigrationSteps } =
    useFetchMigrationSteps({ fetchTemplateUpdates, fetchResourceTypes });

  useEffect(() => {
    fetchMigrationSteps();
  }, [fetchMigrationSteps]);

  return (
    <MigrationContext.Provider
      value={{
        fetchResourceTypes,
        fetchResourceTypesState,
        fetchTemplateUpdates,
        fetchTemplateUpdatesState,
        fetchMigrationSteps,
        fetchMigrationStepsState,
      }}
    >
      {children}
    </MigrationContext.Provider>
  );
};
